import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Trash2,
  Loader2,
  ArrowLeft,
  FileDown,
  Image,
  Layout,
  Palette,
  ChevronDown,
  ChevronUp,
  Search,
  Copy,
  Check,
  Clock,
  Eye,
  FileImage,
  GripVertical,
  Layers,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAT_OPTIONS = [
  { value: '30x30', label: '30x30 cm (kwadrat)' },
  { value: '20x30', label: '20x30 cm (pionowy)' },
  { value: 'A4', label: 'A4 (210x297 mm)' },
];

const STATUS_CONFIG = {
  draft: { label: 'Szkic', color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  review: { label: 'Do akceptacji', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
  approved: { label: 'Zatwierdzony', color: 'bg-green-500/10 text-green-500 border-green-500/20' },
};

const STATUS_FLOW = ['draft', 'review', 'approved'];

// -- Default slot layouts for new pages when no template is selected --
const DEFAULT_LAYOUTS = {
  single: {
    name: 'Jedno zdjecie',
    slots: [{ x: 5, y: 5, w: 90, h: 90 }],
  },
  two_horizontal: {
    name: 'Dwa obok siebie',
    slots: [
      { x: 3, y: 10, w: 45, h: 80 },
      { x: 52, y: 10, w: 45, h: 80 },
    ],
  },
  two_vertical: {
    name: 'Dwa pionowo',
    slots: [
      { x: 10, y: 3, w: 80, h: 45 },
      { x: 10, y: 52, w: 80, h: 45 },
    ],
  },
  three_grid: {
    name: 'Trzy (1+2)',
    slots: [
      { x: 10, y: 3, w: 80, h: 45 },
      { x: 3, y: 52, w: 45, h: 45 },
      { x: 52, y: 52, w: 45, h: 45 },
    ],
  },
  four_grid: {
    name: 'Cztery (siatka)',
    slots: [
      { x: 3, y: 3, w: 45, h: 45 },
      { x: 52, y: 3, w: 45, h: 45 },
      { x: 3, y: 52, w: 45, h: 45 },
      { x: 52, y: 52, w: 45, h: 45 },
    ],
  },
};

const SLOT_COLORS = [
  'bg-blue-500/20 border-blue-500/40',
  'bg-emerald-500/20 border-emerald-500/40',
  'bg-purple-500/20 border-purple-500/40',
  'bg-amber-500/20 border-amber-500/40',
  'bg-rose-500/20 border-rose-500/40',
  'bg-cyan-500/20 border-cyan-500/40',
];

export default function AlbumDesignerPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // -- List view state --
  const [albums, setAlbums] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // -- New album dialog --
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAlbum, setNewAlbum] = useState({
    name: '',
    format: '30x30',
    gallery_id: '',
  });

  // -- Delete confirmation dialog --
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // -- Editor view state --
  const [editorAlbum, setEditorAlbum] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);

  // ========== Data fetching ==========

  const fetchAlbums = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data.albums || []);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac albumow');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGalleries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/galleries`);
      setGalleries(Array.isArray(response.data) ? response.data : response.data.galleries || []);
    } catch {
      // galleries are optional, don't show error
    }
  }, []);

  const fetchAlbumDetail = useCallback(async (albumId) => {
    setEditorLoading(true);
    try {
      const response = await axios.get(`${API}/albums/${albumId}`);
      setEditorAlbum(response.data);
      setSelectedPageIndex(0);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac albumu');
      setEditorAlbum(null);
    } finally {
      setEditorLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async (format) => {
    setTemplatesLoading(true);
    try {
      const response = await axios.get(`${API}/albums/templates`, {
        params: { format },
      });
      setTemplates(response.data.templates || []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlbums();
    fetchGalleries();
  }, [fetchAlbums, fetchGalleries]);

  // Fetch templates when editor opens
  useEffect(() => {
    if (editorAlbum?.format) {
      fetchTemplates(editorAlbum.format);
    }
  }, [editorAlbum?.format, fetchTemplates]);

  // ========== Album CRUD ==========

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    if (!newAlbum.name.trim()) {
      toast.error('Podaj nazwe albumu');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: newAlbum.name.trim(),
        format: newAlbum.format,
        gallery_id: newAlbum.gallery_id || null,
      };
      const response = await axios.post(`${API}/albums`, payload);
      setAlbums((prev) => [response.data, ...prev]);
      setCreateDialogOpen(false);
      setNewAlbum({ name: '', format: '30x30', gallery_id: '' });
      toast.success('Album zostal utworzony');
    } catch (error) {
      toast.error('Nie udalo sie utworzyc albumu');
    } finally {
      setCreating(false);
    }
  };

  const confirmDeleteAlbum = (album) => {
    setAlbumToDelete(album);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAlbum = async () => {
    if (!albumToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/albums/${albumToDelete.id}`);
      setAlbums((prev) => prev.filter((a) => a.id !== albumToDelete.id));
      setDeleteDialogOpen(false);
      setAlbumToDelete(null);
      // If we were editing this album, go back to list
      if (editorAlbum?.id === albumToDelete.id) {
        setEditorAlbum(null);
      }
      toast.success('Album zostal usuniety');
    } catch (error) {
      toast.error('Nie udalo sie usunac albumu');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!editorAlbum) return;
    try {
      await axios.put(`${API}/albums/${editorAlbum.id}`, { status: newStatus });
      setEditorAlbum((prev) => ({ ...prev, status: newStatus }));
      // Also update in list
      setAlbums((prev) =>
        prev.map((a) => (a.id === editorAlbum.id ? { ...a, status: newStatus } : a))
      );
      toast.success(`Status zmieniony na: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      toast.error('Nie udalo sie zmienic statusu');
    }
  };

  // ========== Page management ==========

  const pages = editorAlbum?.pages || [];
  const selectedPage = pages[selectedPageIndex] || null;

  const handleAddPage = async () => {
    if (!editorAlbum) return;
    setAddingPage(true);
    try {
      const payload = {
        layout_template: 'single',
        background_color: '#ffffff',
      };
      await axios.post(`${API}/albums/${editorAlbum.id}/pages`, payload);
      await fetchAlbumDetail(editorAlbum.id);
      // Select the new page (last one)
      setSelectedPageIndex(pages.length);
      toast.success('Strona dodana');
    } catch (error) {
      toast.error('Nie udalo sie dodac strony');
    } finally {
      setAddingPage(false);
    }
  };

  const handleDeletePage = async () => {
    if (!editorAlbum || !selectedPage) return;
    const pageNumber = selectedPage.page_number ?? selectedPageIndex + 1;
    setDeletingPage(true);
    try {
      await axios.delete(`${API}/albums/${editorAlbum.id}/pages/${pageNumber}`);
      await fetchAlbumDetail(editorAlbum.id);
      // Adjust selection
      if (selectedPageIndex >= pages.length - 1) {
        setSelectedPageIndex(Math.max(0, pages.length - 2));
      }
      toast.success('Strona usunieta');
    } catch (error) {
      toast.error('Nie udalo sie usunac strony');
    } finally {
      setDeletingPage(false);
    }
  };

  const handleUpdatePage = async (updates) => {
    if (!editorAlbum || !selectedPage) return;
    const pageNumber = selectedPage.page_number ?? selectedPageIndex + 1;
    setSavingPage(true);
    try {
      await axios.put(`${API}/albums/${editorAlbum.id}/pages/${pageNumber}`, updates);
      await fetchAlbumDetail(editorAlbum.id);
      toast.success('Strona zaktualizowana');
    } catch (error) {
      toast.error('Nie udalo sie zapisac zmian');
    } finally {
      setSavingPage(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (templateId === 'none') {
      // Use default single layout
      handleUpdatePage({
        layout_template: 'single',
        photo_slots: DEFAULT_LAYOUTS.single.slots,
      });
      return;
    }

    // Check if it's a built-in default layout
    if (DEFAULT_LAYOUTS[templateId]) {
      handleUpdatePage({
        layout_template: templateId,
        photo_slots: DEFAULT_LAYOUTS[templateId].slots,
      });
      return;
    }

    // Find from server templates
    const template = templates.find((tpl) => tpl.id === templateId);
    if (template) {
      handleUpdatePage({
        layout_template: template.id,
        photo_slots: template.layout?.slots || [],
      });
    }
  };

  const handleBackgroundColorChange = (color) => {
    handleUpdatePage({ background_color: color });
  };

  // ========== Export ==========

  const handleExport = async (format) => {
    if (!editorAlbum) return;
    setExporting(true);
    try {
      const endpoint = format === 'pdf' ? 'export-pdf' : 'export-jpg';
      const response = await axios.post(`${API}/albums/${editorAlbum.id}/${endpoint}`);
      const { status, page_count, photo_count } = response.data;
      toast.success(
        `Eksport ${format.toUpperCase()}: ${page_count} stron, ${photo_count} zdjec — ${status}`
      );
    } catch (error) {
      toast.error(`Nie udalo sie wyeksportowac albumu jako ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  // ========== Helpers ==========

  const getSlots = (page) => {
    if (page?.photo_slots && Array.isArray(page.photo_slots) && page.photo_slots.length > 0) {
      return page.photo_slots;
    }
    const layoutKey = page?.layout_template || 'single';
    return DEFAULT_LAYOUTS[layoutKey]?.slots || DEFAULT_LAYOUTS.single.slots;
  };

  const filteredAlbums = albums.filter((a) =>
    a.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // ========== Loading state ==========

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // ========== Editor view ==========

  if (editorAlbum) {
    if (editorLoading) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <Sidebar activePath="/albums" />

        <main className="lg:ml-64 p-4 lg:p-6">
          {/* Editor Top Bar */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditorAlbum(null);
                    fetchAlbums();
                  }}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl lg:text-2xl font-bold">{editorAlbum.name}</h1>
                    <Badge variant="outline" className="text-xs">
                      {FORMAT_OPTIONS.find((f) => f.value === editorAlbum.format)?.label ||
                        editorAlbum.format}
                    </Badge>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CONFIG[editorAlbum.status]?.color || STATUS_CONFIG.draft.color}`}
                    >
                      {STATUS_CONFIG[editorAlbum.status]?.label || editorAlbum.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {pages.length} {pages.length === 1 ? 'strona' : pages.length < 5 ? 'strony' : 'stron'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {/* Status dropdown */}
                <Select
                  value={editorAlbum.status || 'draft'}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FLOW.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_CONFIG[s].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting || pages.length === 0}
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-1" />
                  )}
                  Eksport PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('jpg')}
                  disabled={exporting || pages.length === 0}
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FileImage className="w-4 h-4 mr-1" />
                  )}
                  Eksport JPG
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Editor Layout: Left Panel + Center + Right Panel */}
          <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-180px)]">
            {/* Left Panel: Page Thumbnails */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="lg:w-56 flex-shrink-0"
            >
              <Card className="bg-card border-border">
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Strony</CardTitle>
                    <span className="text-xs text-muted-foreground">{pages.length}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <ScrollArea className="max-h-[calc(100vh-340px)]">
                    <div className="space-y-2">
                      <AnimatePresence>
                        {pages.map((page, idx) => {
                          const slots = getSlots(page);
                          const bgColor = page.background_color || '#ffffff';
                          return (
                            <motion.div
                              key={page.id || idx}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              transition={{ duration: 0.15 }}
                            >
                              <button
                                type="button"
                                onClick={() => setSelectedPageIndex(idx)}
                                className={`w-full text-left rounded-md border-2 transition-all p-1 ${
                                  selectedPageIndex === idx
                                    ? 'border-primary ring-1 ring-primary/30'
                                    : 'border-border hover:border-muted-foreground/30'
                                }`}
                              >
                                {/* Mini page preview */}
                                <div
                                  className="aspect-square rounded-sm relative overflow-hidden"
                                  style={{ backgroundColor: bgColor }}
                                >
                                  {slots.map((slot, si) => (
                                    <div
                                      key={si}
                                      className={`absolute border border-dashed rounded-sm ${SLOT_COLORS[si % SLOT_COLORS.length]}`}
                                      style={{
                                        left: `${slot.x}%`,
                                        top: `${slot.y}%`,
                                        width: `${slot.w}%`,
                                        height: `${slot.h}%`,
                                      }}
                                    />
                                  ))}
                                </div>
                                <p className="text-[10px] text-center text-muted-foreground mt-1">
                                  {idx + 1}
                                </p>
                              </button>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>

                      {/* Add page button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={handleAddPage}
                        disabled={addingPage}
                      >
                        {addingPage ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3 mr-1" />
                        )}
                        Dodaj strone
                      </Button>

                      {/* Delete selected page button */}
                      {selectedPage && pages.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleDeletePage}
                          disabled={deletingPage}
                        >
                          {deletingPage ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3 mr-1" />
                          )}
                          Usun strone {selectedPageIndex + 1}
                        </Button>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </motion.div>

            {/* Center: Page Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="flex-1 min-w-0"
            >
              <Card className="bg-card border-border h-full">
                <CardContent className="p-4 lg:p-6 flex items-center justify-center h-full">
                  {selectedPage ? (
                    <div className="w-full max-w-xl mx-auto">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-muted-foreground">
                          Strona {selectedPageIndex + 1} z {pages.length}
                        </p>
                        {savingPage && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Zapisywanie...
                          </div>
                        )}
                      </div>

                      {/* Page canvas */}
                      <div
                        className="aspect-square w-full rounded-lg border-2 border-border relative shadow-sm overflow-hidden"
                        style={{
                          backgroundColor: selectedPage.background_color || '#ffffff',
                        }}
                      >
                        {getSlots(selectedPage).map((slot, si) => (
                          <div
                            key={si}
                            className={`absolute border-2 border-dashed rounded-md flex items-center justify-center transition-all ${SLOT_COLORS[si % SLOT_COLORS.length]}`}
                            style={{
                              left: `${slot.x}%`,
                              top: `${slot.y}%`,
                              width: `${slot.w}%`,
                              height: `${slot.h}%`,
                            }}
                          >
                            <div className="text-center">
                              <Image className="w-6 h-6 mx-auto mb-1 opacity-40" />
                              <p className="text-[10px] text-muted-foreground opacity-60">
                                Slot {si + 1}
                              </p>
                              <p className="text-[9px] text-muted-foreground/50">
                                {slot.w}% x {slot.h}%
                              </p>
                            </div>
                          </div>
                        ))}

                        {/* Empty state */}
                        {getSlots(selectedPage).length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center text-muted-foreground">
                              <Layout className="w-10 h-10 mx-auto mb-2 opacity-30" />
                              <p className="text-sm opacity-60">Wybierz szablon ukladu</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Slot info summary */}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {getSlots(selectedPage).map((slot, si) => (
                          <div
                            key={si}
                            className={`text-[10px] px-2 py-1 rounded border ${SLOT_COLORS[si % SLOT_COLORS.length]}`}
                          >
                            Slot {si + 1}: {slot.x}%, {slot.y}% — {slot.w}%x{slot.h}%
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">
                        {pages.length === 0
                          ? 'Album nie ma jeszcze stron. Dodaj pierwsza strone.'
                          : 'Wybierz strone z panelu po lewej'}
                      </p>
                      {pages.length === 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={handleAddPage}
                          disabled={addingPage}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Dodaj strone
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Panel: Template & Settings */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="lg:w-64 flex-shrink-0"
            >
              <div className="space-y-4">
                {/* Template selector */}
                <Card className="bg-card border-border">
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Layout className="w-4 h-4" />
                      Szablon ukladu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 space-y-3">
                    {/* Built-in layouts */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">
                        Podstawowe uklady
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(DEFAULT_LAYOUTS).map(([key, layout]) => {
                          const isActive =
                            selectedPage?.layout_template === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => handleTemplateSelect(key)}
                              disabled={!selectedPage || savingPage}
                              className={`p-2 rounded-md border-2 transition-all text-left ${
                                isActive
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-muted-foreground/30'
                              } ${!selectedPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              {/* Mini layout preview */}
                              <div className="aspect-square relative bg-muted/30 rounded-sm mb-1">
                                {layout.slots.map((slot, si) => (
                                  <div
                                    key={si}
                                    className={`absolute rounded-[2px] ${SLOT_COLORS[si % SLOT_COLORS.length]}`}
                                    style={{
                                      left: `${slot.x}%`,
                                      top: `${slot.y}%`,
                                      width: `${slot.w}%`,
                                      height: `${slot.h}%`,
                                    }}
                                  />
                                ))}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {layout.name}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Server templates */}
                    {templates.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground mb-2 block">
                          Szablony z biblioteki
                        </Label>
                        <Select
                          value={
                            selectedPage?.layout_template &&
                            !DEFAULT_LAYOUTS[selectedPage.layout_template]
                              ? selectedPage.layout_template
                              : ''
                          }
                          onValueChange={handleTemplateSelect}
                          disabled={!selectedPage || savingPage}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue placeholder="Wybierz szablon..." />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((tpl) => (
                              <SelectItem key={tpl.id} value={tpl.id}>
                                <div className="flex items-center gap-2">
                                  <span>{tpl.name}</span>
                                  {tpl.category && (
                                    <span className="text-[10px] text-muted-foreground">
                                      ({tpl.category})
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {templatesLoading && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Ladowanie szablonow...
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Background color picker */}
                <Card className="bg-card border-border">
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Kolor tla
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={selectedPage?.background_color || '#ffffff'}
                        onChange={(e) => handleBackgroundColorChange(e.target.value)}
                        disabled={!selectedPage || savingPage}
                        className="w-10 h-10 rounded cursor-pointer border border-border disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <Input
                          value={selectedPage?.background_color || '#ffffff'}
                          onChange={(e) => {
                            if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                              handleBackgroundColorChange(e.target.value);
                            }
                          }}
                          placeholder="#ffffff"
                          disabled={!selectedPage || savingPage}
                          className="h-9 text-sm font-mono"
                        />
                      </div>
                    </div>
                    {/* Quick color presets */}
                    <div className="flex gap-1.5 mt-3 flex-wrap">
                      {[
                        '#ffffff',
                        '#f8f9fa',
                        '#e9ecef',
                        '#1a1a2e',
                        '#16213e',
                        '#0f3460',
                        '#f5f0eb',
                        '#fdf6ec',
                        '#fce4ec',
                        '#e8f5e9',
                        '#e3f2fd',
                        '#f3e5f5',
                      ].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => handleBackgroundColorChange(color)}
                          disabled={!selectedPage || savingPage}
                          className={`w-6 h-6 rounded border transition-all ${
                            (selectedPage?.background_color || '#ffffff') === color
                              ? 'border-primary ring-1 ring-primary/50 scale-110'
                              : 'border-border hover:scale-105'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Page info */}
                {selectedPage && (
                  <Card className="bg-card border-border">
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm font-medium">Informacje o stronie</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <div className="space-y-1.5 text-xs text-muted-foreground">
                        <div className="flex justify-between">
                          <span>Numer strony</span>
                          <span className="font-medium text-foreground">
                            {selectedPageIndex + 1}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Szablon</span>
                          <span className="font-medium text-foreground">
                            {DEFAULT_LAYOUTS[selectedPage.layout_template]?.name ||
                              selectedPage.layout_template ||
                              '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sloty na zdjecia</span>
                          <span className="font-medium text-foreground">
                            {getSlots(selectedPage).length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Kolor tla</span>
                          <div className="flex items-center gap-1">
                            <div
                              className="w-3 h-3 rounded border border-border"
                              style={{
                                backgroundColor: selectedPage.background_color || '#ffffff',
                              }}
                            />
                            <span className="font-mono font-medium text-foreground">
                              {selectedPage.background_color || '#ffffff'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // ========== List view ==========

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/albums" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Albumy</h1>
            <p className="text-muted-foreground mt-1">
              Projektuj i zarzadzaj albumami fotograficznymi
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nowy album
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          {[
            {
              label: 'Wszystkie',
              value: albums.length,
              icon: BookOpen,
            },
            {
              label: 'Szkice',
              value: albums.filter((a) => a.status === 'draft').length,
              icon: Clock,
            },
            {
              label: 'Do akceptacji',
              value: albums.filter((a) => a.status === 'review').length,
              icon: Eye,
            },
            {
              label: 'Zatwierdzone',
              value: albums.filter((a) => a.status === 'approved').length,
              icon: Check,
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj albumow..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 max-w-md"
          />
        </div>

        {/* Albums Grid */}
        {filteredAlbums.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAlbums.map((album, idx) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
              >
                <Card className="bg-card border-border hover:border-primary/30 transition-all group cursor-pointer">
                  <CardContent className="p-0">
                    {/* Album preview - layout visualization */}
                    <div
                      className="aspect-video bg-secondary/30 relative overflow-hidden"
                      onClick={() => fetchAlbumDetail(album.id)}
                    >
                      {/* Show a mini preview of the first page layout or default */}
                      <div className="absolute inset-4 rounded-sm bg-white/80 dark:bg-white/10 shadow-inner">
                        {(album.first_page_slots || DEFAULT_LAYOUTS.single.slots).map(
                          (slot, si) => (
                            <div
                              key={si}
                              className={`absolute rounded-sm ${SLOT_COLORS[si % SLOT_COLORS.length]}`}
                              style={{
                                left: `${slot.x}%`,
                                top: `${slot.y}%`,
                                width: `${slot.w}%`,
                                height: `${slot.h}%`,
                              }}
                            />
                          )
                        )}
                      </div>

                      {/* Page count badge */}
                      <div className="absolute bottom-3 right-3 bg-black/70 rounded-sm px-2 py-1">
                        <span className="text-xs text-white">
                          {album.page_count || 0}{' '}
                          {(album.page_count || 0) === 1
                            ? 'strona'
                            : (album.page_count || 0) < 5
                              ? 'strony'
                              : 'stron'}
                        </span>
                      </div>

                      {/* Format badge */}
                      <div className="absolute top-3 left-3 bg-black/70 rounded-sm px-2 py-1">
                        <span className="text-xs text-white">{album.format}</span>
                      </div>
                    </div>

                    {/* Album Info */}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3
                            className="font-semibold truncate cursor-pointer hover:text-primary"
                            onClick={() => fetchAlbumDetail(album.id)}
                          >
                            {album.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(album.created_at)}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <GripVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => fetchAlbumDetail(album.id)}>
                              <Layout className="w-4 h-4 mr-2" />
                              Edytuj album
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => confirmDeleteAlbum(album)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Usun album
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-3">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CONFIG[album.status]?.color || STATUS_CONFIG.draft.color}`}
                        >
                          {STATUS_CONFIG[album.status]?.label || 'Szkic'}
                        </span>
                        {album.gallery_name && (
                          <span className="text-xs text-muted-foreground truncate">
                            {album.gallery_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {search ? 'Nie znaleziono albumow' : 'Brak albumow'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {search
                  ? 'Sprobuj zmienic kryteria wyszukiwania'
                  : 'Utwórz pierwszy album, aby rozpoczac projektowanie'}
              </p>
              {!search && (
                <Button onClick={() => setCreateDialogOpen(true)} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Nowy album
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* New Album Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nowy album</DialogTitle>
            <DialogDescription>
              Utwórz nowy album fotograficzny. Wybierz format i opcjonalnie powiaz z galeria.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAlbum} className="space-y-4">
            <div className="space-y-2">
              <Label>Nazwa albumu *</Label>
              <Input
                placeholder="np. Album slubny — Jan i Anna"
                value={newAlbum.name}
                onChange={(e) => setNewAlbum((prev) => ({ ...prev, name: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Format</Label>
              <Select
                value={newAlbum.format}
                onValueChange={(v) => setNewAlbum((prev) => ({ ...prev, format: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz format" />
                </SelectTrigger>
                <SelectContent>
                  {FORMAT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Galeria (opcjonalnie)</Label>
              <Select
                value={newAlbum.gallery_id}
                onValueChange={(v) => setNewAlbum((prev) => ({ ...prev, gallery_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz galerie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Brak</SelectItem>
                  {galleries.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Utwórz album
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Usunac album?</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunac album{' '}
              <span className="font-semibold text-foreground">
                {albumToDelete?.name}
              </span>
              ? Tej operacji nie mozna cofnac.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setAlbumToDelete(null);
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAlbum}
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Usun album
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
